import { useEffect, useImperativeHandle, useMemo } from 'react';

import { Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { VectorImageName } from '~assets/vectors/generated';
import VectorImage from '~components/media/vectorImage';
import { useFormContainer } from '~hooks';
import { Dimens } from '~themes';

export type ExportFormat = 'xlsx' | 'csv' | 'pdf';
export type ExportStatus = 'selecting' | 'exporting';
export const exportFormatExtensions: Record<ExportFormat, string> = {
	xlsx: '.xlsx',
	csv: '.csv',
	pdf: '.pdf',
};

export const getExportFormatExtension = (format: ExportFormat): string =>
	exportFormatExtensions[format];

interface ExportFormProps {
	status: ExportStatus;
	selectedFormat: ExportFormat | null;
	onFormatChange: (format: ExportFormat | null) => void;
	onSubmit: (format: ExportFormat) => void;
	onCancel: () => void;
}

const ExportForm = ({
	status,
	selectedFormat,
	onFormatChange,
	onSubmit,
	onCancel,
}: ExportFormProps) => {
	const { t } = useTranslation();
	const formatOptions: { value: ExportFormat; label: string; icon: VectorImageName }[] =
		useMemo(() => {
			return [
				{ value: 'xlsx', label: t('filetype.excel', { ns: 'system' }), icon: 'filetype/excel' },
				{ value: 'csv', label: t('filetype.csv', { ns: 'system' }), icon: 'filetype/csv' },
				{ value: 'pdf', label: t('filetype.pdf', { ns: 'system' }), icon: 'filetype/pdf' },
			];
		}, [t]);

	const theme = useTheme();
	const { setDisabled, formRef } = useFormContainer();

	useImperativeHandle(
		formRef,
		() => ({
			onSubmit: () => {
				if (selectedFormat) {
					onSubmit(selectedFormat);
				}
			},
			onCancel: () => onCancel(),
		}),
		[selectedFormat, onSubmit, onCancel]
	);

	useEffect(() => {
		setDisabled?.(selectedFormat == null || status === 'exporting');
	}, [selectedFormat, status, setDisabled]);

	const selectedOption = formatOptions.find((o) => o.value === selectedFormat);

	// Exporting state — show selected icon + generating text
	if (status === 'exporting') {
		return (
			<Stack alignItems='center' spacing={Dimens.spacingSm}>
				{selectedOption && <VectorImage name={selectedOption.icon} size={48} original />}
				<Typography variant='paragraphSm' color={theme.palette.textTertiary.main}>
					{t('generatingExport', { ns: 'actions' })}
				</Typography>
			</Stack>
		);
	}

	// Selecting state — show toggle buttons
	return (
		<Stack spacing={Dimens.spacing3xl}>
			<Typography variant='paragraph' align='center' color={theme.palette.textTertiary.main}>
				{t('selectFileTypeToExport', { ns: 'actions' })}
			</Typography>
			<ToggleButtonGroup
				value={selectedFormat}
				exclusive
				onChange={(_e, value: ExportFormat | null) => {
					if (value !== null) {
						onFormatChange(value);
					}
				}}
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					gap: Dimens.spacingLg,
					minWidth: { xs: 'unset', sm: '380px' },
					'& .MuiToggleButtonGroup-grouped': {
						border: `1px solid ${theme.palette.borderSecondary.main}`,
						color: theme.palette.textTertiary.main,
						borderRadius: Dimens.radiusMd,
						'&.Mui-selected': {
							border: `1px solid ${theme.palette.borderPrimarySolid.main}`,
							backgroundColor: theme.palette.bgSecondary.main,
							color: theme.palette.textPrimary.main,
							'&:hover': {
								backgroundColor: theme.palette.bgPrimaryHover.main,
							},
						},
						'&:hover': {
							backgroundColor: theme.palette.bgPrimaryHover.main,
						},
					},
				}}
			>
				{formatOptions.map((option) => (
					<ToggleButton
						key={option.value}
						value={option.value}
						sx={{
							flex: 1,
							display: 'flex',
							flexDirection: { xs: 'row', sm: 'column' },
							justifyContent: { xs: 'flex-start', sm: 'center' },
							gap: Dimens.spacingSm,
							py: Dimens.spacingLg,
							px: Dimens.spacingXl,
							textAlign: { xs: 'left', sm: 'center' },
						}}
					>
						<VectorImage name={option.icon} size={Dimens.spacing6xl} original />
						<Typography variant='label' sx={{ textTransform: 'none' }}>
							{option.label}
						</Typography>
					</ToggleButton>
				))}
			</ToggleButtonGroup>
		</Stack>
	);
};

export default ExportForm;
